import React, { ReactElement, ReactNode, useState } from 'react';

import parse, { HTMLReactParserOptions } from 'html-react-parser';
import { marked } from 'marked';

import { ReferencesDict, ReferenceType } from '../../../../backend/src/types/types';
import { referencingLinkRegex } from '@/constants/referencing';
import { cn } from '@/new/lib/utils';
import { REFERENCE_TYPE_STYLES } from '@/new/pages/matters/_matterId/subsets/_subsetId/new-kim/_threadId/components/reference-styles';
import { useStore } from '@/store';

interface Reference {
  id?: string;
  type?: 'fact' | 'chunk';
}

// Component for grouping multiple references together
const ReferencesGroup = ({ references }: { references: ReactNode[] }) => {
  const [expanded, setExpanded] = useState(false);
  if (expanded) {
    return <>{references}</>;
  }
  return (
    <span
      onClick={() => setExpanded(true)}
      className={cn(
        'mr-0.5 ml-0.75 inline-block -translate-y-px cursor-pointer rounded-md border border-gray-300 bg-white px-1 font-sans text-[10px] font-normal text-gray-500 duration-150',
        REFERENCE_TYPE_STYLES[ReferenceType.Fact].chip,
        REFERENCE_TYPE_STYLES[ReferenceType.Fact].chipHover,
      )}
    >
      …
    </span>
  );
};

/**
 * Unified function to format references in text
 * Always parses markdown, groups successive references
 * Always uses drawer for references
 */
export const TextWithReferences = ({
  content,
  referencesDict,
  onReferenceClick,
  disableReferenceClick = false,
}: {
  content: string;
  referencesDict?: ReferencesDict;
  onReferenceClick?: (reference: Reference) => void;
  disableReferenceClick?: boolean;
}) => {
  // Process content synchronously using useMemo to avoid re-processing on every render
  const formattedContent = React.useMemo(() => {
    if (!content) return '';

    // Use marked.parse synchronously
    let htmlContent = marked.parse(content, { async: false }) as string;
    const olRegex = /<ol>/g;
    if (olRegex.test(htmlContent)) {
      htmlContent = htmlContent.replace(/<ol>/g, '<ol className="list-decimal">');
    }
    return htmlContent;
  }, [content]);

  return processReferences(formattedContent, { referencesDict, onReferenceClick, disableReferenceClick });
};

// Internal function to process references in HTML content
const processReferences = (
  htmlContent: string,
  options: {
    referencesDict?: ReferencesDict;
    onReferenceClick?: (reference: Reference) => void;
    disableReferenceClick?: boolean;
  },
): React.ReactNode => {
  const { openReferenceDrawer } = useStore();
  const { referencesDict, onReferenceClick } = options || {};

  const handleReferenceClick = (reference: Reference) => {
    if (options.disableReferenceClick) {
      return;
    }
    if (reference.type === 'chunk') {
      openReferenceDrawer({ chunkId: reference.id });
    } else {
      openReferenceDrawer({ factId: reference.id });
    }
    onReferenceClick?.(reference);
  };

  // Function to create reference elements
  const createReferenceElement = (referenceNum: number, index: number, id: string): ReactElement => {
    const ref = referencesDict?.[referenceNum];
    const referenceType = ref?.type === 'chunk' ? ReferenceType.Chunk : ReferenceType.Fact;
    const style = REFERENCE_TYPE_STYLES[referenceType];

    // Styled to match the new Kim references (white chip with a per-type tint).
    return (
      <span
        key={`reference-${referenceNum}-${index}`}
        className={cn(
          'mr-0.5 ml-0.75 inline-block -translate-y-px rounded-md border border-gray-300 bg-white px-1 font-sans text-[10px] font-normal text-gray-500',
          style.chip,
          !options.disableReferenceClick && `cursor-pointer duration-150 ${style.chipHover}`,
        )}
        onClick={(e) => {
          e.stopPropagation();
          handleReferenceClick({ id: ref?.id || id, type: ref?.type || 'fact' });
        }}
      >
        {referenceNum}
      </span>
    );
  };

  // Parse HTML and replace references
  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      if (domNode.type === 'text') {
        const text = domNode.data;
        const elements: React.ReactNode[] = [];
        let lastIndex = 0;

        // Find all references in the text
        for (const match of text.matchAll(referencingLinkRegex)) {
          // Add text before the reference
          if (match.index !== undefined && match.index > lastIndex) {
            elements.push(text.slice(lastIndex, match.index));
          }

          // Single reference number
          const referenceNum = parseInt(match[1], 10);
          const id = match[2]; // From the regex capture group

          elements.push(createReferenceElement(referenceNum, match.index!, id));

          lastIndex = match.index! + match[0].length;
        }

        // Add remaining text
        if (lastIndex < text.length) {
          elements.push(text.slice(lastIndex));
        }

        // Only return if we found references
        if (elements.length > 0 && lastIndex > 0) {
          return <span className="inline">{elements}</span>;
        }
      }
      return domNode;
    },
  };

  const parsed = parse(htmlContent, parserOptions);
  return sweepAndMergeReferences(parsed);
};

// Constants for reference grouping. Only collapse runs of 6+ consecutive
// references into a "…" so short runs stay fully visible.
const REFERENCE_GROUP_THRESHOLD = 5;

// Helper function to check if a child is a reference element
const isReferenceElement = (child: React.ReactNode): boolean => {
  return (
    React.isValidElement(child) &&
    Boolean(child.key) &&
    typeof child.key === 'string' &&
    child.key.startsWith('.$reference-')
  );
};

// Helper function to process a group of references
const processReferenceGroup = (referenceGroup: React.ReactNode[], groupIndex: number): React.ReactNode => {
  if (referenceGroup.length > REFERENCE_GROUP_THRESHOLD) {
    return <ReferencesGroup key={`group-${groupIndex}`} references={[...referenceGroup]} />;
  }
  return referenceGroup;
};

// Function for grouping consecutive references to improve readability
const sweepAndMergeReferences = (content: string | JSX.Element | JSX.Element[]): React.ReactNode => {
  // Handle primitive content types
  if (typeof content === 'string') {
    return content;
  }

  if (Array.isArray(content)) {
    return content.map((item) => sweepAndMergeReferences(item));
  }

  // Handle JSX elements
  if (
    !React.isValidElement(content) ||
    !('children' in (content.props as any)) ||
    (content.props as any).children === undefined ||
    (content.props as any).children === null
  ) {
    return content;
  }

  const children = React.Children.toArray((content.props as any).children as React.ReactNode);
  const updatedChildren: React.ReactNode[] = [];
  let consecutiveReferences: React.ReactNode[] = [];

  // Process each child element
  children.forEach((child, index) => {
    if (child == null || child == undefined || (typeof child === 'string' && child.trim() === '')) {
      return;
    }

    if (isReferenceElement(child)) {
      // Add reference to current group
      consecutiveReferences.push(child);
    } else {
      // Non-reference element found - process any accumulated references
      if (consecutiveReferences.length > 0) {
        updatedChildren.push(processReferenceGroup(consecutiveReferences, index));
        consecutiveReferences = [];
      }

      // Process the non-reference child recursively
      updatedChildren.push(sweepAndMergeReferences(child as JSX.Element));
    }
  });

  // Handle any remaining references at the end
  if (consecutiveReferences.length > 0) {
    updatedChildren.push(processReferenceGroup(consecutiveReferences, children.length));
  }

  // Reconstruct the element with processed children
  return React.cloneElement(content as React.ReactElement<any>, {
    ...(content.props as any),
    children: updatedChildren,
  });
};
